// src/components/SurveyEditor.js
import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Button, Card, Form, Nav, Tab, ListGroup, Alert} from 'react-bootstrap';
import axios from 'axios';
import {useAuth} from "../AuthContext";
import { Trash2, FileText, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;


const SurveyEditor = () => {
    const { id } = useParams(); // Get survey ID from URL, if present
    const navigate = useNavigate();
    const { user } = useAuth();
    const [selectedFile, setSelectedFile] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);


    const [survey, setSurvey] = useState({
        language: 'English (US)',
        context: '',
        title: '',
        tone: 'Professional',
        welcomeMessage: 'Welcome! Click the button below to get started.',
        aiPowered: false,
        questions: [],
        attachedPDFs: [], // Store uploaded PDFs
        logoUrl: '', // Company logo URL
        theme: 'default', // Survey theme
    });

    // Load existing survey if an ID is provided
    useEffect(() => {
        if (id) {
            user.getIdToken().then((idToken) => {
                // Fetch survey data from backend by ID
                axios.get(`${API_URL}/api/surveys/${id}`,{headers: {Authorization: `Bearer ${idToken}`}})
                    .then(response => setSurvey(response.data.data))
                    .catch(error => console.error("Error fetching survey:", error));
            })
        }
    }, [id]);


    const uploadPDFToGCS = async () => {
        if (!selectedFile) {
            setError('Please select a PDF file to upload.');
            return;
        }

        try {
            setLoading(true);
            let originalFileName = selectedFile.name;

            // Extract file extension
            const fileExtension = originalFileName.split('.').pop();

            // Get name without extension and sanitize it
            const nameWithoutExtension = originalFileName
                .substring(0, originalFileName.lastIndexOf('.'))
                .toLowerCase()
                .replace(/[^a-z0-9-]/g, '');

            // Create sanitized filename with extension
            let fileName = `${nameWithoutExtension}.${fileExtension}`;

            user.getIdToken().then(async (idToken) => {
                // Step 1: Get Signed URL from Backend
                // File name may only contain lowercase alphanumeric characters or dashes (-) and cannot begin or end with a dash
                const response = await fetch(`${API_URL}/api/generate-signed-url`, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json', Authorization: `Bearer ${idToken}`},
                    body: JSON.stringify({
                        originalFileName: originalFileName,
                        fileName: fileName,
                        fileType: selectedFile.type,
                    }),
                });

                if (!response.ok) {
                    throw new Error('Failed to generate signed URL');
                }

                const {url, publicUrl} = await response.json();

                // Step 2: Upload File to GCS using Signed URL
                const uploadResponse = await fetch(url, {
                    method: 'PUT',
                    headers: {'Content-Type': selectedFile.type},
                    body: selectedFile,
                });

                if (!uploadResponse.ok) {
                    throw new Error('Failed to upload file to GCS');
                }
                // attachedPDFs could be undefined
                if (!survey.attachedPDFs) {
                    survey.attachedPDFs = [];
                }

                // Step 3: Store File URL in Survey Data
                setSurvey((prev) => ({
                    ...prev,
                    attachedPDFs: [...prev.attachedPDFs, {
                        originalFileName: originalFileName,
                        fileName: fileName,
                        fileType: selectedFile.type,
                        publicUrl: publicUrl
                    }],
                }));

                setSelectedFile(null);
                alert('File uploaded successfully!');
            })
        } catch (error) {
            console.error('Error uploading file:', error);
            setError('Failed to upload file. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.type === 'application/pdf') {
                setSelectedFile(file);
                setError('');
            } else {
                setError('Only PDF files are allowed!');
                setSelectedFile(null);
            }
        }
    };
    const handleInputChange = (event) => {
        const { name, value, type, checked } = event.target;
        setSurvey(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const getDownloadUrl = async (pdf) => {
        try {
            const idToken = await user.getIdToken();
            const response = await axios.post(
                `${API_URL}/api/generate-download-signed-url`,
                {
                    fileName: pdf.fileName,
                    fileType: pdf.fileType
                },
                { headers: { Authorization: `Bearer ${idToken}` } }
            );

            // Create a temporary anchor element
            const link = document.createElement('a');
            link.href = response.data.signedUrl;

            // Use the original filename for the download
            link.setAttribute('download', pdf.originalFileName);

            // Append to body, click, and remove
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading file:', error);
            setError('Failed to download file. Please try again.');
        }
    };


    const handleSkippableChange = (index, isSkippable) => {
        const updatedQuestions = survey.questions.map((q, i) =>
            i === index ? { ...q, isSkippable } : q
        );
        setSurvey((prev) => ({ ...prev, questions: updatedQuestions }));
    };
    const handleMaxFollowUps = (index, number) => {
        const updatedQuestions = survey.questions.map((q, i) =>
            i === index ? { ...q, maxFollowUps:number } : q
        );
        setSurvey((prev) => ({ ...prev, questions: updatedQuestions }));
    };
    const handleDesiredClarity = (index, value) => {
        const updatedQuestions = survey.questions.map((q, i) =>
            i === index ? { ...q, desiredClarity:value } : q
        );
        setSurvey((prev) => ({ ...prev, questions: updatedQuestions }));
    };

    const handleRemoveFile = (index) => {
        if (window.confirm("Are you sure you want to delete this file?")) {
            setSurvey((prevSurvey) => ({
                ...prevSurvey,
                attachedPDFs: prevSurvey.attachedPDFs.filter((_, i) => i !== index),
            }));
        }
    };

    const addQuestion = () => {
        setSurvey((prev) => ({
            ...prev,
            questions: [...prev.questions, { questionText: '', questionType: 'text', isSkippable: false }],
        }));
    };


    // Handle question deletion by index
    const deleteQuestion = (indexToDelete) => {
        setSurvey((prevSurvey) => ({
            ...prevSurvey,
            questions: prevSurvey.questions.filter((_, index) => index !== indexToDelete),
        }));
    };

    const generateQuestions = () => {
        // Call the /api/questions/generate endpoint with context to generate questions
        let survey_context = survey.context
        if (survey.questions.length > 0 ){
            survey_context += " consider these existing questions " + survey.questions.map(q => q.questionText).join(" ")
        }

        user.getIdToken().then((idToken) => {
            axios.post(`${API_URL}/api/questions/generate`, {context: survey_context,
                numQuestions:5, tone: survey.tone,
                files: survey.attachedPDFs
            }, {headers: {Authorization: `Bearer ${idToken}`}})
                .then(response => {
                    // Add the new questions to the questions array
                    setSurvey(prev => ({
                        ...prev,
                        questions: [...prev.questions, ...response.data.data]
                    }));
                })
                .catch(error => console.error("Error generating questions:", error));
        })
    };

    const handleQuestionChange = (index, questionText) => {
        const updatedQuestions = survey.questions.map((q, i) =>
            i === index ? { ...q, questionText } : q
        );
        setSurvey(prev => ({ ...prev, questions: updatedQuestions }));
    };

    const handleSave = () => {
        if (id) {
            user.getIdToken().then((idToken) => {
                // Update existing survey
                axios.patch(`${API_URL}/api/surveys/${id}`, survey,{headers: {Authorization: `Bearer ${idToken}`}})
                    .then(() => {
                        alert('Survey updated successfully!');
                        navigate('/'); // Redirect to dashboard after saving
                    })
                    .catch(error => console.error("Error updating survey:", error));
            })
        } else {
            user.getIdToken().then((idToken) => {

                // Create a new survey
                axios.post(`${API_URL}/api/surveys`, survey,{headers: {Authorization: `Bearer ${idToken}`}})
                    .then(() => {
                        alert('Survey created successfully!');
                        navigate('/'); // Redirect to dashboard after creation
                    })
                    .catch(error => console.error("Error creating survey:", error));
            })}
    };

    return (
        <div className="d-flex">
            <div className="container p-3" style={{ width: '60%' }}>
                <h1>{id ? 'Configure Survey' : 'Create New Survey'}</h1>

                <Tab.Container defaultActiveKey="conversation">
                    <Nav variant="tabs" className="mb-3">
                        <Nav.Item>
                            <Nav.Link eventKey="conversation">Conversation</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="design">Design</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="logic">Logic</Nav.Link>
                        </Nav.Item>
                    </Nav>

                    <Tab.Content>
                        <Tab.Pane eventKey="conversation">
                            <Card className="p-3 mb-3">
                                <h4>Language</h4>
                                <Form.Select
                                    name="language"
                                    value={survey.language}
                                    onChange={handleInputChange}
                                    className="mb-3"
                                >
                                    <option value="English (US)">English (US)</option>
                                    <option value="Spanish (ES)">Spanish (ES)</option>
                                </Form.Select>

                                <h4>Context and Objective</h4>
                                <Form.Control
                                    type="text"
                                    name="context"
                                    placeholder="e.g., Gather customer feedback"
                                    value={survey.context}
                                    onChange={handleInputChange}
                                    className="mb-3"
                                />

                                <h4>Title</h4>
                                <Form.Control
                                    type="text"
                                    name="title"
                                    placeholder="e.g., Customer Satisfaction Survey"
                                    value={survey.title}
                                    onChange={handleInputChange}
                                    className="mb-3"
                                />

                                <h4>Tone of Voice</h4>
                                <Form.Select
                                    name="tone"
                                    value={survey.tone}
                                    onChange={handleInputChange}
                                    className="mb-3"
                                >
                                    <option value="Professional">Professional</option>
                                    <option value="Formal">Formal</option>
                                    <option value="Casual">Casual</option>
                                    <option value="Personal">Personal</option>
                                    <option value="Friendly">Friendly</option>
                                </Form.Select>

                                <h4>Welcome Message</h4>
                                <Form.Control
                                    as="textarea"
                                    name="welcomeMessage"
                                    value={survey.welcomeMessage}
                                    onChange={handleInputChange}
                                    className="mb-3"
                                    rows={3}
                                />

                            </Card>

                            {/* PDF Upload Section */}
                            <Card className="mb-4">
                                <Card.Header>Attach PDF Files</Card.Header>
                                <Card.Body>
                                    <Form.Group controlId="formFile" className="mb-3">
                                        <Form.Label>Select a PDF file</Form.Label>
                                        <Form.Control type="file" accept=".pdf" onChange={handleFileChange} />
                                    </Form.Group>

                                    {error && <Alert variant="danger">{error}</Alert>}

                                    <Button onClick={uploadPDFToGCS} disabled={!selectedFile || loading}>
                                        {loading ? 'Uploading...' : 'Upload PDF'}
                                    </Button>

                                    {survey.attachedPDFs && survey.attachedPDFs.length > 0 && (
                                        <div className="mt-3">
                                            <h5>Attached Files:</h5>
                                            <ListGroup>
                                                {survey.attachedPDFs.map((pdf, index) => {
                                                    const filename = decodeURIComponent(pdf.originalFileName);

                                                    return (
                                                        <ListGroup.Item
                                                            key={index}
                                                            className="d-flex justify-content-between align-items-center"
                                                            style={{ padding: '10px 15px', borderRadius: '8px', marginBottom: '5px' }}
                                                        >
                                                            <div className="d-flex align-items-center">
                                                                <FileText size={16} className="me-2" />
                                                                <span>{filename}</span>
                                                            </div>

                                                            <div>
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    onClick={() => getDownloadUrl(pdf)}
                                                                    title="View or download file"
                                                                >
                                                                    <Download size={16} />
                                                                </Button>

                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={() => handleRemoveFile(index)}
                                                                    title="Delete file"
                                                                >
                                                                    <Trash2 size={16} />
                                                                </Button>
                                                            </div>
                                                        </ListGroup.Item>
                                                    );
                                                })}
                                            </ListGroup>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>

                            <Card className="p-3">
                                <Card.Header className="bg-dark text-white">Survey Questions</Card.Header>
                                <Card.Body>
                                    <Button variant="primary" onClick={generateQuestions} className="mb-3">
                                        + Generate Questions
                                    </Button>
                                    <Button variant="primary" onClick={addQuestion} className="mb-3">
                                        + Add Question
                                    </Button>
                                    <ListGroup className="mt-3">
                                        {survey.questions.map((q, index) => (
                                            <Card key={index} className="mb-3 shadow-sm p-3">
                                                <div className="d-flex justify-content-between align-items-start">
                                                    {/* Question Text Input */}
                                                    <Form.Control
                                                        type="text"
                                                        placeholder={`Question ${index + 1}`}
                                                        value={q.questionText}
                                                        onChange={(e) => handleQuestionChange(index, e.target.value)}
                                                        className="flex-grow-1"
                                                    />

                                                    {/* Delete Button (Top Right) */}
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => deleteQuestion(index)}
                                                        className="ms-2"
                                                    >
                                                        <Trash2 size={16} />
                                                    </Button>
                                                </div>

                                                {/* Question Settings (Skippable, Follow-Ups, Clarity) */}
                                                <div className="d-flex flex-wrap gap-3 mt-3">
                                                    {/* Skippable Checkbox */}
                                                    <Form.Check
                                                        type="checkbox"
                                                        label="Skippable"
                                                        checked={q.isSkippable}
                                                        onChange={(e) => handleSkippableChange(index, e.target.checked)}
                                                    />

                                                    {/* Max Follow Ups */}
                                                    <Form.Group>
                                                        <Form.Label>Max Follow Ups</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            value={q.maxFollowUps}
                                                            max={5}
                                                            min={0}
                                                            onChange={(e) => handleMaxFollowUps(index, e.target.value)}
                                                            className="w-100"
                                                        />
                                                    </Form.Group>

                                                    {/* Desired Clarity */}
                                                    <Form.Group>
                                                        <Form.Label>Desired Clarity</Form.Label>
                                                        <Form.Select
                                                            value={q.desiredClarity}
                                                            onChange={(e) => handleDesiredClarity(index, e.target.value)}
                                                            className="w-100"
                                                        >
                                                            <option value="low">Low</option>
                                                            <option value="medium">Medium</option>
                                                            <option value="high">High</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </div>
                                            </Card>
                                        ))}
                                    </ListGroup>
                                </Card.Body>
                            </Card>
                        </Tab.Pane>

                        <Tab.Pane eventKey="design">
                            <Card className="p-3">
                                <h4>Design Options</h4>
                                
                                <Form.Group className="mb-3">
                                    <Form.Label>Survey Theme</Form.Label>
                                    <Form.Select
                                        name="theme"
                                        value={survey.theme || 'default'}
                                        onChange={handleInputChange}
                                    >
                                        <option value="default">Default</option>
                                        <option value="corporate">Corporate</option>
                                    </Form.Select>
                                </Form.Group>
                                
                                <Form.Group className="mb-3">
                                    <Form.Label>Company Logo URL</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="logoUrl"
                                        placeholder="Enter logo URL"
                                        value={survey.logoUrl || ''}
                                        onChange={handleInputChange}
                                    />
                                    <Form.Text className="text-muted">
                                        Enter a direct URL to your company logo image (PNG, JPG, etc.)
                                    </Form.Text>
                                </Form.Group>
                                
                                {survey.logoUrl && (
                                    <div className="mb-3">
                                        <Form.Label>Logo Preview</Form.Label>
                                        <div className="text-center p-3 border rounded">
                                            <img 
                                                src={survey.logoUrl} 
                                                alt="Company Logo" 
                                                style={{ maxHeight: '60px', maxWidth: '200px' }}
                                                onError={(e) => {
                                                    e.target.src = 'https://via.placeholder.com/200x60?text=Invalid+Logo+URL';
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </Card>
                        </Tab.Pane>

                        <Tab.Pane eventKey="logic">
                            <Card className="p-3">
                                <h4>Logic Options (Coming Soon)</h4>
                            </Card>
                        </Tab.Pane>
                    </Tab.Content>
                </Tab.Container>

                <Button variant="success" onClick={handleSave} className="mt-3 float-end">
                    {id ? 'Save Changes' : 'Create Survey'}
                </Button>

                <Button className="mt-3" variant="success" onClick={()=>{navigate('/')}  } >
                    Back
                </Button>
            </div>

            <div className="container p-3" style={{ width: '40%', backgroundColor: '#f8f9fa', borderRadius: '8px', boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)' }}>
                <h4>Preview</h4>
                <Card className="p-3 mt-3">
                    <h5>{survey.title || 'Survey Title'}</h5>
                    <p><strong>Language:</strong> {survey.language}</p>
                    <p><strong>Tone:</strong> {survey.tone}</p>
                    <p><strong>Welcome Message:</strong> {survey.welcomeMessage}</p>
                    <h6>Questions:</h6>
                    {survey.questions.length > 0 ? (
                        survey.questions.map((q, index) => (
                            <p key={index}>
                                Q{index + 1}: {q.questionText || 'Your question text'}
                                {q.isSkippable && <span className="text-muted"> (Skippable)</span>}
                            </p>
                        ))
                    ) : (
                        <p>No questions added yet.</p>
                    )}

                    {survey.attachedPDFs && survey.attachedPDFs.length > 0 && (
                        <div className="mt-3">
                            <h6>Attached Files:</h6>
                            <ul>
                                {survey.attachedPDFs.map((pdf, index) => (
                                    <li key={index}>
                                        {decodeURIComponent(pdf.originalFileName)}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default SurveyEditor;